/**
 * StockFlow Helper
 * Utilities for managing inventory movements and line items
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTranType, getStockDirection } from '../config/tranTypes';
import { getCached, invalidateCache, updateCached } from './fyDataCache';
import { recordReads } from './readTelemetry';
import { getItemsCached } from './masterDataStore';
import { ACTIVE_STOCK_STATUSES, activeFyId, inActiveFy } from './stockStatus';
import { PRODUCTION_TYPES, costRowsChange, commitPoolUpdates, buildPoolFromRows, validFyKey, snapshotAverage, poolOf } from './itemCostEngine';

// Speed (2026-09-30): location look-ups used on every stock save are kept for
// 10 minutes on this device (they were read from the server on every save).
const _locCache = new Map(); // key -> { at, value }
const _LOC_TTL_MS = 10 * 60 * 1000;
const _locGet = (k) => { const h = _locCache.get(k); return (h && Date.now() - h.at < _LOC_TTL_MS) ? h.value : undefined; };
const _locSet = (k, v) => { _locCache.set(k, { at: Date.now(), value: v }); };
/** Called by Branch Setup / Inventory Locations after a change. */
export const clearStockLocationCache = () => { _locCache.clear(); };

// Background average-cost refreshes still running (see generateStockFlow). If the
// user tries to close the tab during one, the browser asks them to wait.
let _pendingCostRefresh = 0;
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('beforeunload', (e) => {
    if (_pendingCostRefresh > 0) { e.preventDefault(); e.returnValue = ''; }
  });
}

// Stock statuses + financial-year scope now live in ./stockStatus (shared with
// the costing engine). Re-exported here so every existing import keeps working.
export { ACTIVE_STOCK_STATUSES, activeFyId, inActiveFy } from './stockStatus';

/**
 * Get default location for company
 */
export const getDefaultLocation = async (companyId) => {
  const cachedLoc = _locGet(`def|${companyId}`);
  if (cachedLoc) return cachedLoc;
  const found = await getDefaultLocationLive(companyId);
  if (found && found.id) _locSet(`def|${companyId}`, found);
  return found;
};

const getDefaultLocationLive = async (companyId) => {
  const locationsRef = collection(db, 'companies', companyId, 'inventoryLocations');
  const q = query(locationsRef, where('isDefault', '==', true));
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
  }

  // If no default, check if "Default" location exists
  const defaultQuery = query(locationsRef, where('locationName', '==', 'Default'));
  const defaultSnapshot = await getDocs(defaultQuery);

  if (!defaultSnapshot.empty) {
    return {
      id: defaultSnapshot.docs[0].id,
      ...defaultSnapshot.docs[0].data()
    };
  }

  // Create default location
  const defaultLocation = {
    locationCode: 'DEF-001',
    locationName: 'Default',
    locationType: 'Warehouse',
    isDefault: true,
    isActive: true,
    createdAt: new Date().toISOString()
  };

  const docRef = await addDoc(locationsRef, defaultLocation);
  return {
    id: docRef.id,
    ...defaultLocation
  };
};

/**
 * The location assigned to a branch in Branch Setup, or null if none set.
 * Used so a branch's sales deduct stock from its own location (Branch → Location).
 */
export const getBranchLocation = async (companyId, branchId) => {
  const key = `br|${companyId}|${branchId}`;
  const hit = _locGet(key);
  if (hit !== undefined) return hit;
  try {
    const snap = await getDoc(doc(db, 'companies', companyId, 'branches', branchId));
    if (!snap.exists()) { _locSet(key, null); return null; }
    const b = snap.data();
    const v = b.defaultLocationId ? { id: b.defaultLocationId, name: b.defaultLocationName || '' } : null;
    _locSet(key, v);
    return v;
  } catch {
    return null;
  }
};

/**
 * Get item's costing method and current cost
 */
export const getItemCostingInfo = async (companyId, itemId) => {
  // Speed: use the item list already kept on this device (no server trip);
  // read live only if the item isn't there yet (e.g. just created elsewhere).
  try {
    const all = await getItemsCached(companyId);
    const it = (all || []).find(x => x.id === itemId);
    if (it) {
      return {
        costingMethod: it.costingMethod || 'weightedAverage',
        currentCost: it.averageCost || it.purchasePrice || 0,
        purchasePrice: it.purchasePrice || 0,
        salePrice: it.salePrice || 0
      };
    }
  } catch (e) { /* fall through to the live read */ }
  const itemRef = doc(db, 'companies', companyId, 'inventoryItems', itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return {
      costingMethod: 'weightedAverage',
      currentCost: 0
    };
  }

  const item = itemDoc.data();
  return {
    costingMethod: item.costingMethod || 'weightedAverage',
    currentCost: item.averageCost || item.purchasePrice || 0,
    purchasePrice: item.purchasePrice || 0,
    salePrice: item.salePrice || 0
  };
};

/**
 * Get current stock quantity for an item
 * @param {string} [branchId] - Optional branch ID to filter stock by branch
 */
export const getCurrentStock = async (companyId, itemId, locationId = null, branchId = null) => {
  const stockRef = collection(db, 'companies', companyId, 'stockFlow');
  let conditions = [
    where('itemId', '==', itemId),
    where('status', 'in', ACTIVE_STOCK_STATUSES)
  ];

  if (locationId) {
    conditions.push(where('locationId', '==', locationId));
  }

  if (branchId) {
    conditions.push(where('branchId', '==', branchId));
  }

  const q = query(stockRef, ...conditions);
  const snapshot = await getDocs(q);

  let quantityIn = 0;
  let quantityOut = 0;
  let totalCostIn = 0;

  const fy = activeFyId();
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    if (!inActiveFy(data, fy)) return; // don't double-count last year's opening stock
    if (data.direction === 'in') {
      quantityIn += data.quantity || 0;
      totalCostIn += data.totalCost || 0;
    } else if (data.direction === 'out') {
      quantityOut += data.quantity || 0;
    }
  });

  const quantity = quantityIn - quantityOut;

  return {
    quantity,
    totalCost: totalCostIn,
    averageCost: quantityIn > 0 ? totalCostIn / quantityIn : 0
  };
};

/**
 * Generate StockFlow entries for a transaction
 */
export const generateStockFlow = async (companyId, transactionData, userId, opts = {}) => {
  const {
    transactionId,
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    locationId,
    locationName,
    status,
    lines
  } = transactionData;

  const tranType = getTranType(tranTypeCode);
  if (!tranType) {
    return [];
  }
  // Note: the decision "should this transaction post stock?" is made upstream
  // in transactionHelper via shouldPostStock() which honors the Inventory
  // Movement setting. Here we only check that the tran type has a direction —
  // if it doesn't, there's nothing to post.

  // Get direction from transaction type
  let direction = getStockDirection(tranTypeCode);
  if (!direction) {
    return [];
  }

  // Resolve the location this stock movement should hit.
  let finalLocationId = locationId;
  let finalLocationName = locationName;

  // BRANCH → LOCATION rule: when the transaction didn't specify a location
  // (e.g. POS sales), deduct from the location assigned to its branch in
  // Branch Setup. This keeps each branch's stock in its own location instead of
  // draining one shared default. Falls through only if the branch has none set.
  if (!finalLocationId && transactionData.branchId) {
    const branchLoc = await getBranchLocation(companyId, transactionData.branchId);
    if (branchLoc?.id) {
      finalLocationId = branchLoc.id;
      finalLocationName = branchLoc.name;
    }
  }

  if (!finalLocationId) {
    const defaultLocation = await getDefaultLocation(companyId);
    finalLocationId = defaultLocation.id;
    finalLocationName = defaultLocation.locationName;
  }

  const timestamp = new Date().toISOString();
  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const savedEntries = [];

  // Filter valid lines.
  // Service items (itemType === 'service') never produce a stockFlow row —
  // they're tagged by enrichTransactionLines() so we don't need to refetch.
  const validLines = lines.filter(line => line.itemId && line.itemType !== 'service');
  if (validLines.length === 0) return savedEntries;

  // Pre-fetch all item costing info in PARALLEL (instead of sequential per-line)
  const uniqueItemIds = [...new Set(validLines.map(l => l.itemId))];
  const costingInfoMap = {};
  await Promise.all(uniqueItemIds.map(async (itemId) => {
    costingInfoMap[itemId] = await getItemCostingInfo(companyId, itemId);
  }));

  // Pre-fetch stock info in PARALLEL for outgoing items that need average cost.
  // Branch-aware: scope the WAVG cost pool to the source branch so inter-branch cost leakage
  // cannot happen when the same item has different costs at different branches.
  const stockInfoMap = {};
  const txBranchId = transactionData.branchId || null;
  // INV-04: only production documents still use this location/branch read — their
  // cost logic is left exactly as it was. Every other zero-cost stock-out line
  // takes the item's running weighted average (no extra reads).
  const isProduction = PRODUCTION_TYPES.has(tranTypeCode);
  const hasOutLines = isProduction && (direction === 'out' || (direction === 'both' && validLines.some(l => (l.direction || direction) === 'out')));
  if (hasOutLines) {
    const itemsNeedingStock = validLines
      .filter(line => !line.unitCost && (direction === 'out' || line.direction === 'out'))
      .map(line => ({ itemId: line.itemId, branchId: line.branchId || txBranchId || null }));
    const uniqueNeedStock = [...new Map(itemsNeedingStock.map(x => [`${x.itemId}__${x.branchId || ''}`, x])).values()];
    if (uniqueNeedStock.length > 0) {
      await Promise.all(uniqueNeedStock.map(async ({ itemId, branchId }) => {
        const key = `${itemId}__${branchId || ''}`;
        stockInfoMap[key] = await getCurrentStock(companyId, itemId, finalLocationId, branchId);
      }));
    }
  }

  // ── Serial uniqueness guard ──
  // For stock-IN lines with serials, verify none are already in stock (received but not issued).
  // Prevents two concurrent IN docs from binding the same serial.
  const incomingSerialItems = validLines
    .filter(l => {
      const d = l.direction || direction;
      return (d === 'in' || d === 'both') && Array.isArray(l.serialNumbers) && l.serialNumbers.length > 0;
    });
  if (incomingSerialItems.length > 0) {
    for (const line of incomingSerialItems) {
      const existing = await getAvailableSerials(companyId, line.itemId);
      const existingSet = new Set(existing);
      const conflicts = line.serialNumbers.filter(sn => existingSet.has(sn));
      if (conflicts.length > 0) {
        throw new Error(
          `Duplicate serial number(s) for ${line.itemName || line.itemCode || line.itemId}: ${conflicts.join(', ')}. ` +
          `These serials are already in stock. Use unique serials per incoming unit.`
        );
      }
      // Also check in-line duplicates across lines of the same item
      const dupes = line.serialNumbers.filter((sn, idx) => line.serialNumbers.indexOf(sn) !== idx);
      if (dupes.length > 0) {
        throw new Error(
          `Repeated serial(s) within this document for ${line.itemName || line.itemId}: ${[...new Set(dupes)].join(', ')}.`
        );
      }
    }
  }

  // Build all entries, then batch write (or add to the caller's single save — ACC-03)
  const writer = opts.writer || null;
  const batch = writer ? null : writeBatch(db);

  for (let i = 0; i < validLines.length; i++) {
    const line = validLines[i];
    const costingInfo = costingInfoMap[line.itemId];

    // Determine unit cost
    const lineDirection = line.direction || direction;
    let unitCost = line.unitCost || 0;

    // IPI: prefer the pre-computed landed unit cost from landedCostHelper.
    // This already includes foreign cost (PKR) + allocated capitalized charges
    // (customs, freight, clearing, etc.) per unit, so it's the true inventory cost.
    if (tranTypeCode === 'IPI' && line.landedUnitCost) {
      unitCost = line.landedUnitCost;
    }

    // Where a zero-cost line's fallback came from: 'base' = an item-level (base
    // unit) cost, 'entered' = the line's own price in the entered unit.
    let fallbackFrom = null;
    // A line the costing engine already priced keeps that cost, even 0
    // (an item with no cost yet — it is re-priced when stock arrives).
    const enginePriced = !isProduction && line.costSource === 'average';
    if (lineDirection === 'out' && !unitCost && !enginePriced) {
      if (isProduction) {
        const lineBranchId = line.branchId || txBranchId || null;
        const key = `${line.itemId}__${lineBranchId || ''}`;
        const stockInfo = stockInfoMap[key];
        unitCost = (stockInfo && stockInfo.averageCost) || costingInfo.currentCost;
      } else {
        unitCost = costingInfo.currentCost; // item's running weighted average (INV-04)
        fallbackFrom = 'base';
      }
    }

    if (lineDirection === 'in' && !unitCost && !enginePriced) {
      if (tranTypeCode === 'SR' || tranTypeCode === 'NSR') {
        // A return with no cost comes back at the item's average, not its price.
        unitCost = costingInfo.currentCost;
        fallbackFrom = 'base';
      } else {
        unitCost = line.unitPrice || costingInfo.purchasePrice || 0;
        fallbackFrom = line.unitPrice ? 'entered' : 'base';
      }
    }

    // ─── Multi-unit base-canonical storage ───
    // If the line was enriched (has baseQuantity/baseUnitCost from enrichLineWithUnit),
    // store BASE-unit values in the stockFlow row so WAVG, current stock, and
    // ledger reports remain coherent regardless of the entered unit. The user's
    // entered values are preserved as audit fields (enteredQuantity, enteredUnitCode).
    const enteredQuantity = Number(line.quantity) || 0;
    const enteredUnitPrice = Number(line.unitPrice) || 0;
    const enteredUnitCost = Number(unitCost) || 0;
    const enteredUnitCode = line.unitCode || line.unitOfMeasure || '';
    const conversion = Number(line.conversion) || 1;

    const hasMultiUnit = line.baseQuantity !== undefined && line.baseQuantity !== null;
    const quantity = hasMultiUnit ? (Number(line.baseQuantity) || 0) : enteredQuantity;
    // INV-04 fix: a zero-cost line used to lose its fallback cost here (the
    // unit enrichment had already set baseUnitCost = 0), so it posted at 0.
    // Production keeps its old behaviour untouched.
    let baseUnitCost = hasMultiUnit && line.baseUnitCost !== undefined && line.baseUnitCost !== null
      ? Number(line.baseUnitCost) || 0
      : enteredUnitCost;
    if (!isProduction && fallbackFrom && hasMultiUnit && !(Number(line.unitCost) > 0)) {
      baseUnitCost = fallbackFrom === 'base'
        ? enteredUnitCost
        : (Number(line.baseUnitPrice) || (conversion > 0 ? enteredUnitCost / conversion : enteredUnitCost));
    }
    const baseUnitPrice = hasMultiUnit && line.baseUnitPrice !== undefined && line.baseUnitPrice !== null
      ? Number(line.baseUnitPrice) || 0
      : enteredUnitPrice;
    // From here on, unitCost/unitPrice in the stockFlow row reflect BASE units.
    unitCost = baseUnitCost;
    const unitPrice = baseUnitPrice || (lineDirection === 'out' ? costingInfo.salePrice : 0);
    const totalCost = quantity * unitCost;
    const grossAmount = quantity * unitPrice;
    const discountAmount = line.discountAmount || 0;
    const netAmount = grossAmount - discountAmount;
    const taxAmount = line.taxAmount || 0;
    const lineTotal = netAmount + taxAmount;
    const profitAmount = lineDirection === 'out' ? (unitPrice - unitCost) * quantity - discountAmount : 0;

    const stockFlowEntry = {
      transactionId,
      tranTypeCode,
      docNumber,
      docDate,
      financialYearId,
      branchId: line.branchId || transactionData.branchId || null,
      branchName: line.branchName || transactionData.branchName || null,
      lineNumber: i + 1,

      // Item info
      itemId: line.itemId,
      itemCode: line.itemCode || '',
      itemName: line.itemName || '',
      description: line.description || '',

      // Location
      locationId: line.locationId || finalLocationId,
      locationName: line.locationName || finalLocationName,

      // For Stock Transfer
      fromLocationId: line.fromLocationId || null,
      fromLocationName: line.fromLocationName || null,
      toLocationId: line.toLocationId || null,
      toLocationName: line.toLocationName || null,

      // Quantity & Direction
      direction: line.direction || direction,
      quantity,
      // Multi-unit: row's `unitOfMeasure` is the BASE unit (since `quantity` is base).
      // Falls back to the line's entered unit when no multi-unit enrichment is present.
      unitOfMeasure: line.baseUnitCode || line.unitOfMeasure || 'Pcs',

      // Multi-unit audit fields — let reports/ledgers show "10 Ctn (240 Pcs)"
      // while keeping `quantity`/`unitCost` as base-unit canonical values.
      enteredQuantity,
      enteredUnitCode: enteredUnitCode || (line.unitOfMeasure || ''),
      enteredUnitPrice,
      enteredUnitCost,
      conversion,
      // Show Two Units: same qty in the item's second unit (display only).
      ...(line.secondUnitCode ? { secondUnitCode: line.secondUnitCode, secondQuantity: Number(line.secondQuantity) || 0 } : {}),

      // Costing
      costingMethod: costingInfo.costingMethod,
      unitCost,
      totalCost,

      // Pricing
      unitPrice,
      grossAmount,

      // Discount
      discountPercent: line.discountPercent || 0,
      discountAmount,
      netAmount,

      // Tax
      taxable: line.taxable !== false,
      taxRate: line.taxRate || 0,
      taxAmount,

      // Totals
      lineTotal,
      profitAmount: Math.max(0, profitAmount),

      // Batch/Serial
      batchNumber: line.batchNumber || '',
      serialNumbers: line.serialNumbers || [],
      expiryDate: line.expiryDate || null,

      // Status
      status: status || 'Draft',

      // Audit
      createdAt: timestamp,
      createdBy: userId
    };
    // INV-04: part of this sale went out while the item was out of stock — its
    // cost is re-priced when stock arrives.
    if (lineDirection === 'out' && Number(line.costPendingQty) > 0) {
      stockFlowEntry.costPendingQty = Number(line.costPendingQty);
      stockFlowEntry.costPending = true;
    }

    // Deterministic IDs (transactionId + line position) make stock-movement
    // regeneration idempotent — a retry / double-click overwrites the same rows
    // instead of creating duplicate movements. Falls back to a random ID only
    // if transactionId is missing.
    const newDocRef = transactionId
      ? doc(stockFlowRef, `${transactionId}__sf${i}`)
      : doc(stockFlowRef);
    if (writer) writer.set(newDocRef, stockFlowEntry); else batch.set(newDocRef, stockFlowEntry);
    savedEntries.push({ id: newDocRef.id, ...stockFlowEntry });

    // For Stock Transfer, create reverse entry at destination
    if (tranTypeCode === 'ST' && line.toLocationId) {
      const transferInEntry = {
        ...stockFlowEntry,
        lineNumber: stockFlowEntry.lineNumber + 0.5,
        direction: 'in',
        locationId: line.toLocationId,
        locationName: line.toLocationName || '',
        // Use destination branch for IN entry (branch-to-branch transfer)
        branchId: line.toBranchId || transactionData.toBranchId || stockFlowEntry.branchId,
        branchName: line.toBranchName || transactionData.toBranchName || stockFlowEntry.branchName
      };

      const transferDocRef = transactionId
        ? doc(stockFlowRef, `${transactionId}__sf${i}_in`)
        : doc(stockFlowRef);
      if (writer) writer.set(transferDocRef, transferInEntry); else batch.set(transferDocRef, transferInEntry);
      savedEntries.push({ id: transferDocRef.id, ...transferInEntry });
    }
  }

  const afterWrite = async () => {
    // INV-04: the item's running average is now updated in the SAME save as the
    // stock rows (see itemCostEngine) — no background full recalculation.

    // Stock changed — fold this posting INTO the cached badge summary instead of
    // wiping it. The wipe made every save force a full stockFlow re-scan on the
    // next form open (data-entry devices never got a cache hit — measured as the
    // #2 fleet read cost). Applying the delta keeps badges exact at 0 reads.
    // Dashboard cache deliberately NOT invalidated (2026-07-17 D1).
    applyEntriesToStockCache(companyId, savedEntries, +1);
    // Per-item on-demand quantities are now stale for the items just moved.
    // Done here rather than in each form so EVERY caller is covered.
    invalidateItemStock(companyId);
  };

  if (writer) {
    // Side effects run only after the whole document has been saved. The
    // caller (createTransaction / updateTransaction) adds the item cost
    // updates to the same save.
    writer.afterCommit(afterWrite);
  } else {
    // Standalone (stock transfer, send / receive, production order): the item
    // running averages go in the same batch as the rows (INV-04).
    const after = [];
    const poolOps = [];
    try {
      await costRowsChange(companyId, {
        update: (ref, data) => poolOps.push({ ref, data }),
        afterCommit: (fn) => after.push(fn),
      }, { added: savedEntries, financialYearId });
    } catch (e) {
      console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
    }
    // Same save as the rows, unless that would pass Firestore's 500-writes limit.
    const poolInBatch = savedEntries.length + poolOps.length <= 480;
    if (poolInBatch) poolOps.forEach(o => batch.update(o.ref, o.data));
    // Single batch commit for all entries
    await batch.commit();
    if (!poolInBatch) await commitPoolUpdates(poolOps);
    for (const fn of after) { try { await fn(); } catch (e) { /* best effort */ } }
    await afterWrite();
  }

  return savedEntries;
};

/**
 * Fold posted/deleted stockFlow rows into any live cached stock summaries
 * (same aggregation math as getStockSummary). sign +1 = rows written,
 * sign -1 = rows deleted. Anything under the prefix that isn't one of our
 * summary arrays (e.g. POS balance keys) is dropped — old invalidate behavior.
 *
 * Exported so writers that build their own stockFlow rows outside
 * generateStockFlow (the POS fast-write path) can fold their committed rows in
 * too, instead of blanket-invalidating every badge cache for the company.
 */
export const applyEntriesToStockCache = (companyId, entries, sign) => {
  try {
    const active = (entries || []).filter(
      e => e && e.itemId && ACTIVE_STOCK_STATUSES.includes(e.status)
    );
    if (active.length === 0) return; // Draft/Cancelled rows never appear in summaries
    updateCached(`stockSummary|${companyId}|`, (value, key) => {
      const parts = key.split('|');
      if (parts.length !== 4 || !Array.isArray(value)) return false; // not ours → drop
      let criteria;
      try { criteria = JSON.parse(parts[3]); } catch (_) { return false; }
      const keyFy = parts[2];
      for (const e of active) {
        // Mirror getStockSummary's scoping exactly (inActiveFy: legacy rows
        // without a year count everywhere; tagged rows only in their own year).
        if (keyFy !== 'nofy' && e.financialYearId && e.financialYearId !== keyFy) continue;
        if (criteria.branchId && e.branchId !== criteria.branchId) continue;
        if (criteria.locationId && e.locationId !== criteria.locationId) continue;
        const rowKey = criteria.byLocation ? `${e.itemId}_${e.locationId}` : e.itemId;
        let row = value.find(
          r => (criteria.byLocation ? `${r.itemId}_${r.locationId}` : r.itemId) === rowKey
        );
        if (!row) {
          if (sign < 0) continue; // deleting rows for an item the cache never saw
          row = {
            itemId: e.itemId, itemCode: e.itemCode, itemName: e.itemName,
            locationId: e.locationId, locationName: e.locationName,
            quantityIn: 0, quantityOut: 0, totalCostIn: 0, totalCostOut: 0,
            currentQuantity: 0, currentValue: 0, averageCost: 0
          };
          value.push(row);
        }
        if (e.direction === 'in') {
          row.quantityIn += sign * (e.quantity || 0);
          row.totalCostIn += sign * (e.totalCost || 0);
        } else if (e.direction === 'out') {
          row.quantityOut += sign * (e.quantity || 0);
          row.totalCostOut += sign * (e.totalCost || 0);
        }
        row.currentQuantity = row.quantityIn - row.quantityOut;
        // INV-04: keep the weighted average the summary was built with (a sale
        // never changes it); old receipts average only when there is none.
        if (!(row.averageCost > 0)) row.averageCost = row.quantityIn > 0 ? row.totalCostIn / row.quantityIn : 0;
        row.currentValue = row.currentQuantity * row.averageCost;
      }
      return true;
    });
  } catch (_) {
    // Any surprise → the old (expensive but always-correct) behavior.
    invalidateCache(`stockSummary|${companyId}`);
  }
};

/**
 * Rebuild the running weighted average of these items for one financial year
 * from their stock rows, and stamp the item card snapshot (averageCost /
 * currentStock). Used by screens that write stock rows directly (Opening
 * Balances manual save + Excel import). INV-04.
 */
export const refreshItemCostSnapshots = async (companyId, itemIds, financialYearId = null) => {
  const uniqueItemIds = [...new Set((itemIds || []).filter(Boolean))];
  const fy = validFyKey(financialYearId || activeFyId());
  if (uniqueItemIds.length === 0 || !fy) return;
  const timestamp = new Date().toISOString();
  await Promise.all(uniqueItemIds.map(async (itemId) => {
    const itemRef = doc(db, 'companies', companyId, 'inventoryItems', itemId);
    const snap = await getDoc(itemRef);
    if (!snap.exists()) return;
    const item = { id: itemId, ...snap.data() };
    if (item.itemType === 'service') return;
    const pool = await buildPoolFromRows(companyId, item, fy);
    const data = {
      [`costState.${fy}`]: { ...pool, init: true, t: timestamp },
    };
    if (!activeFyId() || fy === activeFyId() || !item.costFy || item.costFy === fy) {
      data.averageCost = Math.round(snapshotAverage(pool) * 1e6) / 1e6;
      data.currentStock = pool.q;
      data.costFy = fy;
      data.costUpdatedAt = timestamp;
      data.updatedAt = timestamp;
    }
    await updateDoc(itemRef, data);
  }));
};

/**
 * Delete all StockFlow entries for a transaction
 */
/** Existing stock rows of a transaction (for all-or-nothing edits, ACC-03). */
export const listStockFlowRows = async (companyId, transactionId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'stockFlow'), where('transactionId', '==', transactionId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteStockFlow = async (companyId, transactionId) => {
  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const q = query(stockFlowRef, where('transactionId', '==', transactionId));
  const snapshot = await getDocs(q);

  const deletedRows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  const batch = writeBatch(db);
  snapshot.docs.forEach(doc => {
    batch.delete(doc.ref);
  });
  // INV-04: take the deleted rows out of the items' running averages in the
  // same batch (or right after it, for a very large document).
  const poolOps = [];
  try {
    await costRowsChange(companyId, { update: (ref, data) => poolOps.push({ ref, data }) }, { removed: deletedRows });
  } catch (e) {
    console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
  }
  const poolInBatch = deletedRows.length + poolOps.length <= 480;
  if (poolInBatch) poolOps.forEach(o => batch.update(o.ref, o.data));

  await batch.commit();
  if (!poolInBatch) await commitPoolUpdates(poolOps);

  // Reverse the deleted rows out of the cached badge summaries (see
  // applyEntriesToStockCache) instead of wiping — the wipe forced a full
  // stockFlow re-scan on the next form open after every edit/delete.
  applyEntriesToStockCache(companyId, deletedRows, -1);
  // Same as generateStockFlow: covers edits, cancels and deletes for every caller.
  invalidateItemStock(companyId);

  return { deleted: snapshot.size };
};

/**
 * Get StockFlow entries for a transaction
 */
export const getStockFlow = async (companyId, transactionId) => {
  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const q = query(
    stockFlowRef,
    where('transactionId', '==', transactionId)
  );
  const snapshot = await getDocs(q);

  // Sort client-side to avoid composite index requirement
  const entries = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  entries.sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));

  return entries;
};

/**
 * Get StockFlow by criteria (for reports)
 */
export const getStockFlowByCriteria = async (companyId, criteria = {}) => {
  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const conditions = [];

  if (criteria.financialYearId) {
    conditions.push(where('financialYearId', '==', criteria.financialYearId));
  }

  if (criteria.status) {
    if (Array.isArray(criteria.status)) {
      conditions.push(where('status', 'in', criteria.status));
    } else {
      conditions.push(where('status', '==', criteria.status));
    }
  }

  if (criteria.itemId) {
    conditions.push(where('itemId', '==', criteria.itemId));
  }

  if (criteria.locationId) {
    conditions.push(where('locationId', '==', criteria.locationId));
  }

  if (criteria.tranTypeCode) {
    conditions.push(where('tranTypeCode', '==', criteria.tranTypeCode));
  }

  if (criteria.direction) {
    conditions.push(where('direction', '==', criteria.direction));
  }

  if (criteria.branchId) {
    conditions.push(where('branchId', '==', criteria.branchId));
  }

  // Server-side date bounds (2026-07-16, Firebase cost): opt-in fromDate/toDate
  // ('YYYY-MM-DD'). ⚠️ Only for movement-list style reads — stock BALANCE math
  // (openings, on-hand) needs flows from before the range and must NOT pass
  // these. May require new composite indexes depending on the caller's other
  // filters — verify on first use.
  if (criteria.fromDate) {
    conditions.push(where('docDate', '>=', criteria.fromDate));
  }
  if (criteria.toDate) {
    conditions.push(where('docDate', '<=', criteria.toDate));
  }

  let q;
  if (conditions.length > 0) {
    q = query(stockFlowRef, ...conditions);
  } else {
    q = stockFlowRef;
  }

  const snapshot = await getDocs(q);
  recordReads(companyId, snapshot.size);

  const rows = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // Auto-scope to the ACTIVE financial year (same rule as getStockSummary) unless
  // the caller named a year or explicitly asked for allYears. Without this, reports
  // built on this helper (Inventory Ledger, Stock Details, Serial/Batch tracking)
  // count LAST year's opening stock on top of this year's and show double.
  // Filtered here in JS, not as another `where`, to avoid new composite indexes.
  if (criteria.financialYearId || criteria.allYears) return rows;
  const fy = activeFyId();
  return fy ? rows.filter(r => inActiveFy(r, fy)) : rows;
};

/**
 * Get stock summary by item
 */
export const getStockSummary = async (companyId, criteria = {}) => {
  const all = await getStockFlowByCriteria(companyId, {
    ...criteria,
    status: ACTIVE_STOCK_STATUSES
  });

  // Scope to the active financial year unless the caller asked for a specific one
  // (reports pass their own) or explicitly opted out with allYears: true.
  const fy = criteria.financialYearId || (criteria.allYears ? null : activeFyId());
  const entries = fy ? all.filter(e => inActiveFy(e, fy)) : all;

  const summary = {};

  entries.forEach(entry => {
    const key = criteria.byLocation
      ? `${entry.itemId}_${entry.locationId}`
      : entry.itemId;

    if (!summary[key]) {
      summary[key] = {
        itemId: entry.itemId,
        itemCode: entry.itemCode,
        itemName: entry.itemName,
        locationId: entry.locationId,
        locationName: entry.locationName,
        quantityIn: 0,
        quantityOut: 0,
        totalCostIn: 0,
        totalCostOut: 0,
        currentQuantity: 0,
        currentValue: 0,
        averageCost: 0
      };
    }

    if (entry.direction === 'in') {
      summary[key].quantityIn += entry.quantity || 0;
      summary[key].totalCostIn += entry.totalCost || 0;
    } else if (entry.direction === 'out') {
      summary[key].quantityOut += entry.quantity || 0;
      summary[key].totalCostOut += entry.totalCost || 0;
    }

    summary[key].currentQuantity = summary[key].quantityIn - summary[key].quantityOut;
    summary[key].averageCost = summary[key].quantityIn > 0
      ? summary[key].totalCostIn / summary[key].quantityIn
      : 0;
    summary[key].currentValue = summary[key].currentQuantity * summary[key].averageCost;
  });

  // INV-04: value stock at the item's weighted average (one cost per item for
  // the whole company) — the running average the costing engine keeps, read
  // from the item list already on this device (no extra reads). Items the
  // engine has not priced yet fall back to stock value ÷ quantity, then to
  // the old receipts average.
  const rows = Object.values(summary);
  let itemsById = null;
  try {
    const all = await getItemsCached(companyId);
    itemsById = new Map((all || []).map(i => [i.id, i]));
  } catch (e) { itemsById = null; }
  const byItem = {};
  rows.forEach(r => {
    const t = byItem[r.itemId] || (byItem[r.itemId] = { qIn: 0, qOut: 0, vIn: 0, vOut: 0 });
    t.qIn += r.quantityIn; t.qOut += r.quantityOut; t.vIn += r.totalCostIn; t.vOut += r.totalCostOut;
  });
  const avgOf = {};
  Object.keys(byItem).forEach(itemId => {
    const it = itemsById ? itemsById.get(itemId) : null;
    const poolFy = fy || (it && it.costFy) || null;
    const pool = it && poolFy ? poolOf(it, poolFy) : null;
    let avg = pool ? snapshotAverage(pool) : 0;
    if (!(avg > 0)) {
      const t = byItem[itemId];
      const netQ = t.qIn - t.qOut;
      const netV = t.vIn - t.vOut;
      if (netQ > 0 && netV > 0) avg = netV / netQ;
    }
    avgOf[itemId] = avg;
  });
  rows.forEach(r => {
    if (avgOf[r.itemId] > 0) {
      r.averageCost = avgOf[r.itemId];
      r.currentValue = r.currentQuantity * r.averageCost;
    }
  });

  return rows;
};

/**
 * On-hand quantity for ONE item — the on-demand replacement for the whole-
 * collection scan that used to run on every transaction-form mount.
 *
 * Cost: reads only that item's movements (tens to a few hundred rows) instead
 * of every stock row the company ever wrote (~95,000 on a large company), and
 * only when the user actually asks. Cached for the session so re-opening the
 * same item's stock, and the availability check at Post, are free.
 *
 * Scope matches getStockSummary exactly — active statuses + active financial
 * year — so the number shown on the badge is the same number the availability
 * check uses.
 */
export const getItemStockCached = async (companyId, itemId, criteria = {}) => {
  if (!companyId || !itemId) return 0;
  const { branchId = null, locationId = null, forceRefresh = false } = criteria;
  const key = `itemStock|${companyId}|${activeFyId() || 'nofy'}|${itemId}|${branchId || 'all'}|${locationId || 'all'}`;
  const info = await getCached(
    key,
    () => getCurrentStock(companyId, itemId, locationId, branchId),
    undefined,
    forceRefresh
  );
  return Number(info?.quantity) || 0;
};

/** Drop the cached per-item quantities for a company (after posting stock). */
export const invalidateItemStock = (companyId) => {
  invalidateCache(`itemStock|${companyId}|`);
};

/**
 * Get available batches for an item (for stock-OUT forms)
 * Returns batches with remaining quantity > 0
 */
// Document types whose lines represent a real sale / purchase price for an item.
const SALE_PRICE_TYPES = ['SI', 'NSI', 'PS'];
const PURCHASE_PRICE_TYPES = ['PI', 'NPI', 'IPI'];

/**
 * Recent transacted prices for an item, for on-invoice reference (read-only).
 *   mode 'sale'     → last sale unit prices  (SI / NSI / POS)
 *   mode 'purchase' → last purchase unit costs (PI / NPI / Import PI)
 * Reuses the existing (itemId + status) stockFlow index — NO new composite index.
 * Takes the `limitN` most recent movements of the right type across ALL years and
 * parties, deduped by document, then reads those source transactions once each to
 * attach the party name. Returns [{ price, date, docNumber, partyName }] newest-first.
 * Never throws — returns [] on any error so it can't disrupt the invoice form.
 */
export const getRecentItemPrices = async (companyId, itemId, mode = 'sale', limitN = 4) => {
  if (!companyId || !itemId) return [];
  const types = mode === 'purchase' ? PURCHASE_PRICE_TYPES : SALE_PRICE_TYPES;
  try {
    const stockRef = collection(db, 'companies', companyId, 'stockFlow');
    // Hints are cosmetic, so scope to the active year first — a fast-moving
    // item's full history is hundreds of rows across years and this fires on
    // EVERY item pick. Only when the item had no activity this year (slow
    // mover) fall back to the all-years read so its last known price still
    // shows.
    const fy = activeFyId();
    let snap = null;
    if (fy) {
      snap = await getDocs(query(
        stockRef,
        where('itemId', '==', itemId),
        where('status', 'in', ACTIVE_STOCK_STATUSES),
        where('financialYearId', '==', fy)
      ));
      recordReads(companyId, snap.size);
    }
    if (!snap || snap.empty) {
      snap = await getDocs(query(
        stockRef,
        where('itemId', '==', itemId),
        where('status', 'in', ACTIVE_STOCK_STATUSES)
      ));
      recordReads(companyId, snap.size);
    }
    const rows = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => types.includes(r.tranTypeCode))
      .sort((a, b) => {
        const dc = (b.docDate || '').localeCompare(a.docDate || '');
        return dc !== 0 ? dc : (b.docNumber || '').localeCompare(a.docNumber || '');
      });

    // One row per source document (an item can appear twice on one invoice).
    const seen = new Set();
    const picked = [];
    for (const r of rows) {
      if (r.transactionId && seen.has(r.transactionId)) continue;
      if (r.transactionId) seen.add(r.transactionId);
      picked.push(r);
      if (picked.length >= limitN) break;
    }

    return await Promise.all(picked.map(async (r) => {
      const price = mode === 'purchase'
        ? (Number(r.enteredUnitCost) || Number(r.unitCost) || 0)
        : (Number(r.enteredUnitPrice) || Number(r.unitPrice) || 0);
      let partyName = '';
      if (r.transactionId) {
        try {
          const txSnap = await getDoc(doc(db, 'companies', companyId, 'transactions', r.transactionId));
          if (txSnap.exists()) partyName = txSnap.data().partyName || '';
        } catch { /* party is best-effort */ }
      }
      return { price, date: r.docDate || '', docNumber: r.docNumber || '', partyName };
    }));
  } catch (e) {
    console.warn('getRecentItemPrices failed:', e?.message || e);
    return [];
  }
};

export const getAvailableBatches = async (companyId, itemId, locationId = null, branchId = null) => {
  const conditions = [
    where('itemId', '==', itemId),
    where('status', 'in', ACTIVE_STOCK_STATUSES)
  ];
  if (locationId) {
    conditions.push(where('locationId', '==', locationId));
  }
  if (branchId && branchId !== 'all') {
    conditions.push(where('branchId', '==', branchId));
  }

  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const q = query(stockFlowRef, ...conditions);
  const snapshot = await getDocs(q);
  // Deliberately NOT year-scoped: batches bought last year must stay sellable.
  recordReads(companyId, snapshot.size);

  const batchMap = {};

  snapshot.docs.forEach(doc => {
    const data = doc.data();
    const batch = data.batchNumber;
    if (!batch) return;

    if (!batchMap[batch]) {
      batchMap[batch] = {
        batchNumber: batch,
        expiryDate: data.expiryDate || '',
        qtyIn: 0,
        qtyOut: 0,
        remaining: 0
      };
    }

    if (data.direction === 'in') {
      batchMap[batch].qtyIn += data.quantity || 0;
      if (data.expiryDate && !batchMap[batch].expiryDate) {
        batchMap[batch].expiryDate = data.expiryDate;
      }
    } else if (data.direction === 'out') {
      batchMap[batch].qtyOut += data.quantity || 0;
    }
  });

  return Object.values(batchMap)
    .map(b => ({ ...b, remaining: b.qtyIn - b.qtyOut }))
    .filter(b => b.remaining > 0)
    .sort((a, b) => (a.expiryDate || '').localeCompare(b.expiryDate || ''));
};

/**
 * Get available serial numbers for an item (for stock-OUT forms)
 * Returns serials that are currently in stock (received but not issued)
 */
export const getAvailableSerials = async (companyId, itemId, locationId = null, branchId = null) => {
  const conditions = [
    where('itemId', '==', itemId),
    where('status', 'in', ACTIVE_STOCK_STATUSES)
  ];
  if (locationId) {
    conditions.push(where('locationId', '==', locationId));
  }
  // Branch scope: when caller passes a specific branchId, restrict to that branch
  // so the same physical location used across branches doesn't leak stock.
  if (branchId && branchId !== 'all') {
    conditions.push(where('branchId', '==', branchId));
  }

  const stockFlowRef = collection(db, 'companies', companyId, 'stockFlow');
  const q = query(stockFlowRef, ...conditions);
  const snapshot = await getDocs(q);
  // Deliberately NOT year-scoped: serials received last year must stay sellable.
  recordReads(companyId, snapshot.size);

  // Count each serial's movements (2026-11-04). The old "ever came in and never
  // went out" test kept a serial gone for good once it was sold — a Sale Return
  // (or buying it back after a Purchase Return) never made it available again.
  // Available now = came in more times than it went out.
  const net = new Map();
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    const serials = data.serialNumbers || [];
    if (serials.length === 0) return;
    const step = data.direction === 'in' ? 1 : (data.direction === 'out' ? -1 : 0);
    if (!step) return;
    serials.forEach(sn => { net.set(sn, (net.get(sn) || 0) + step); });
  });

  const available = [];
  net.forEach((n, sn) => { if (n > 0) available.push(sn); });

  return available.sort();
};

export default {
  getDefaultLocation,
  getItemCostingInfo,
  getCurrentStock,
  generateStockFlow,
  deleteStockFlow,
  getStockFlow,
  getStockFlowByCriteria,
  getStockSummary,
  getAvailableBatches,
  getAvailableSerials
};
